import {
  countStrayBraces,
  decodeEntities,
  migrateLegacyPlaceholders,
  parseTemplateFields,
} from './templateFields';

const ACTION_WORDS = [
  'analyze',
  'based',
  'create',
  'define',
  'describe',
  'draft',
  'explain',
  'generate',
  'identify',
  'include',
  'list',
  'outline',
  'provide',
  'summarize',
  'use',
  'write',
];
const ACTION_WORD_RES = ACTION_WORDS.map((word) => new RegExp(`\\b${word}\\b`));
const BLOCK_TAG_RE = /<(h[1-6]|p|li|td|th|blockquote|div)\b([^>]*)>([\s\S]*?)<\/\1>/gi;
const HEADING_TAG_RE = /<h([1-6])\b[^>]*>/gi;

/**
 * Build a stable issue object for template quality feedback.
 * @param {string} id - Unique issue identifier.
 * @param {'error' | 'warning' | 'info'} severity - Issue severity.
 * @param {string} message - User-facing issue message.
 * @returns {{ id: string, severity: 'error' | 'warning' | 'info', message: string }} Template quality issue.
 */
function createIssue(id, severity, message) {
  return { id, severity, message };
}

/**
 * Normalize HTML into readable text without needing a DOM.
 * @param {string} htmlContent - Template HTML or plain text.
 * @returns {string} Plain text with collapsed whitespace.
 */
function getPlainText(htmlContent) {
  return decodeEntities(String(htmlContent || '').replace(/<[^>]*>/g, ' '))
    .replace(/ /g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Return true when prompt text includes an action-oriented instruction.
 * @param {string} prompt - Template prompt text.
 * @returns {boolean} True when the prompt appears actionable.
 */
function hasActionWord(prompt) {
  const normalized = prompt.toLowerCase();
  return ACTION_WORD_RES.some((re) => re.test(normalized));
}

/**
 * Compute prompt-related quality metrics and issues.
 * @param {Array<{instruction: string}>} fields - Parsed template fields.
 * @returns {{ score: number, issues: Array<object>, duplicatePromptCount: number, weakPromptCount: number }} Prompt quality result.
 */
function scorePrompts(fields) {
  const issues = [];
  const prompts = fields.map((field) => field.instruction).filter(Boolean);

  if (prompts.length === 0) {
    return {
      score: 0,
      issues: [createIssue('missing-prompts', 'error', 'Add at least one AI field (toolbar "AI field" or type /field).')],
      duplicatePromptCount: 0,
      weakPromptCount: 0,
    };
  }

  let score = 40;
  const normalizedPrompts = prompts.map((prompt) => prompt.toLowerCase().replace(/\s+/g, ' ').trim());
  const duplicatePromptCount = prompts.length - new Set(normalizedPrompts).size;
  const weakPromptCount = prompts.filter((prompt) => {
    const wordCount = prompt.split(/\s+/).filter(Boolean).length;
    return wordCount < 4 || prompt.length < 18 || !hasActionWord(prompt);
  }).length;

  if (prompts.length === 1) {
    score -= 8;
    issues.push(createIssue('single-prompt', 'warning', 'Add AI fields to each major section so generated content is balanced.'));
  }

  if (weakPromptCount > 0) {
    score -= Math.min(18, weakPromptCount * 6);
    issues.push(createIssue('weak-prompts', 'warning', 'Make field instructions more specific with action words, context, and expected detail.'));
  }

  if (duplicatePromptCount > 0) {
    score -= Math.min(12, duplicatePromptCount * 4);
    issues.push(createIssue('duplicate-prompts', 'warning', 'Use distinct instructions instead of repeating the same field.'));
  }

  return { score: Math.max(0, score), issues, duplicatePromptCount, weakPromptCount };
}

/**
 * Score document structure and field coverage.
 * Coverage = share of headings that are followed by at least one field before the next heading.
 * @param {string} html - Migrated template HTML.
 * @param {number} fieldCount - Number of fields.
 * @returns {{ score: number, issues: Array<object>, headingCount: number, sectionCount: number, emptyParagraphCount: number, coveredSectionCount: number }} Structural quality result.
 */
function scoreStructure(html, fieldCount) {
  const issues = [];
  const headingLevels = [];
  HEADING_TAG_RE.lastIndex = 0;
  let headingMatch = HEADING_TAG_RE.exec(html);
  while (headingMatch !== null) {
    headingLevels.push(Number(headingMatch[1]));
    headingMatch = HEADING_TAG_RE.exec(html);
  }

  let sectionCount = 0;
  let emptyParagraphCount = 0;
  BLOCK_TAG_RE.lastIndex = 0;
  let blockMatch = BLOCK_TAG_RE.exec(html);
  while (blockMatch !== null) {
    const tag = blockMatch[1].toLowerCase();
    const text = getPlainText(blockMatch[3]);
    if (tag === 'p' && !text && !/<(img|div|span)\b/i.test(blockMatch[3])) emptyParagraphCount += 1;
    if (!/^h[1-6]$/.test(tag) && text) sectionCount += 1;
    blockMatch = BLOCK_TAG_RE.exec(html);
  }

  // Split on headings and check each section body for a field.
  const sections = html.split(/<h[1-6]\b[^>]*>[\s\S]*?<\/h[1-6]>/i);
  const bodies = sections.slice(1);
  const coveredSectionCount = bodies.filter((body) => /data-tpl-field=/i.test(body)).length;

  let score = 25;

  if (headingLevels.length === 0) {
    score -= 8;
    issues.push(createIssue('missing-headings', 'warning', 'Add headings so the document has clear sections.'));
  }

  const skippedHeading = headingLevels.some((level, index) => index > 0 && level > headingLevels[index - 1] + 1);
  if (skippedHeading) {
    score -= 5;
    issues.push(createIssue('skipped-heading-level', 'info', 'Use a consistent heading hierarchy without skipping levels.'));
  }

  if (fieldCount > 0 && bodies.length > 1) {
    const sectionsWithContent = bodies.filter((body) => getPlainText(body)).length || bodies.length;
    if (coveredSectionCount / sectionsWithContent < 0.25) {
      score -= 8;
      issues.push(createIssue('low-prompt-coverage', 'warning', 'Add AI fields under more sections so static text does not dominate.'));
    }
  }

  if (emptyParagraphCount >= 3) {
    score -= 4;
    issues.push(createIssue('empty-paragraphs', 'info', 'Remove empty paragraphs to keep the template clean.'));
  }

  return {
    score: Math.max(0, score),
    issues,
    headingCount: headingLevels.length,
    sectionCount,
    emptyParagraphCount,
    coveredSectionCount,
  };
}

/**
 * Score brace safety: any single brace left outside fields and `{{variables}}` is malformed.
 * @param {string} html - Migrated template HTML.
 * @returns {{ score: number, issues: Array<object>, malformedBraceCount: number }} Brace safety result.
 */
function scoreBraceSafety(html) {
  const malformedBraceCount = countStrayBraces(html);
  if (malformedBraceCount === 0) {
    return { score: 20, issues: [], malformedBraceCount };
  }
  return {
    score: 5,
    issues: [createIssue('malformed-braces', 'error', 'Fix unmatched curly braces so every AI instruction is detected as a field.')],
    malformedBraceCount,
  };
}

/**
 * Map a numeric quality score to a label and Material UI color.
 * @param {number} score - Numeric score from 0 to 100.
 * @returns {{ label: string, color: 'error' | 'warning' | 'success' }} Display status.
 */
function getScoreStatus(score) {
  if (score >= 80) return { label: 'Good', color: 'success' };
  if (score >= 55) return { label: 'Needs work', color: 'warning' };
  return { label: 'Bad', color: 'error' };
}

/**
 * Analyze document-template quality using deterministic client-side heuristics.
 * Legacy `{placeholders}` are migrated in memory first, so old and new templates score alike.
 * @param {string} htmlContent - Template HTML content.
 * @returns {{ score: number, label: string, color: 'error' | 'warning' | 'success', metrics: { promptCount: number, fieldCount: number, requiredFieldCount: number, optionalFieldCount: number, blockFieldCount: number, sourcedFieldCount: number, legacyCount: number, variableCount: number, repeatCount: number, duplicatePromptCount: number, weakPromptCount: number, malformedBraceCount: number, headingCount: number, sectionCount: number, coveredSectionCount: number, emptyParagraphCount: number, wordCount: number }, issues: Array<{ id: string, severity: 'error' | 'warning' | 'info', message: string }>, checks: string[] }} Template quality result.
 */
export function analyzeTemplateQuality(htmlContent) {
  const original = String(htmlContent || '');
  const legacyCount = parseTemplateFields(original).legacyCount;
  const html = legacyCount > 0 ? migrateLegacyPlaceholders(original) : original;
  const parsed = parseTemplateFields(html);
  const { fields } = parsed;
  const plainText = getPlainText(html);

  const promptResult = scorePrompts(fields);
  const structureResult = scoreStructure(html, fields.length);
  const braceResult = scoreBraceSafety(html);
  const contentScore = plainText.length > 0 ? 15 : 0;
  const contentIssues = plainText.length > 0
    ? []
    : [createIssue('empty-content', 'error', 'Add template content before scoring quality.')];
  const rawScore = contentScore + promptResult.score + structureResult.score + braceResult.score;
  const cappedScore = fields.length === 0 ? Math.min(rawScore, 45) : rawScore;
  const score = Math.max(0, Math.min(100, Math.round(cappedScore)));
  const status = getScoreStatus(score);

  const requiredFieldCount = fields.filter((field) => field.required).length;
  const sourcedFieldCount = fields.filter((field) => field.source !== 'any').length;
  const checks = [];
  if (fields.length > 0) {
    checks.push(`${fields.length} AI field${fields.length === 1 ? '' : 's'} across ${structureResult.headingCount} heading${structureResult.headingCount === 1 ? '' : 's'}`);
  }
  if (fields.length > 0 && promptResult.weakPromptCount === 0) checks.push('Every field has a specific instruction');
  if (fields.length > 0 && sourcedFieldCount === fields.length) checks.push('Every field states its source');
  if (braceResult.malformedBraceCount === 0) checks.push('No broken or unclosed placeholders');
  if (parsed.variables.length > 0) checks.push(`${parsed.variables.length} variable${parsed.variables.length === 1 ? '' : 's'} filled automatically`);

  return {
    score,
    label: status.label,
    color: status.color,
    metrics: {
      promptCount: fields.length,
      fieldCount: fields.length,
      requiredFieldCount,
      optionalFieldCount: fields.length - requiredFieldCount,
      blockFieldCount: fields.filter((field) => field.isBlock).length,
      sourcedFieldCount,
      legacyCount,
      variableCount: parsed.variables.length,
      repeatCount: parsed.repeats.length,
      duplicatePromptCount: promptResult.duplicatePromptCount,
      weakPromptCount: promptResult.weakPromptCount,
      malformedBraceCount: braceResult.malformedBraceCount,
      headingCount: structureResult.headingCount,
      sectionCount: structureResult.sectionCount,
      coveredSectionCount: structureResult.coveredSectionCount,
      emptyParagraphCount: structureResult.emptyParagraphCount,
      wordCount: plainText ? plainText.split(/\s+/).length : 0,
    },
    issues: [
      ...contentIssues,
      ...promptResult.issues,
      ...braceResult.issues,
      ...structureResult.issues,
    ],
    checks,
  };
}
